/* eslint-disable lingui/no-unlocalized-strings */
/* eslint-disable no-restricted-globals */
import { ENV_VARS } from '@env';
import { isString, mapValues } from 'lodash-es';
import {
  IS_LOCAL_CYPRESS,
  IS_LOCAL_DEV,
  isPRPreview,
  KEY_USER_PREFERENCES,
  KnownDomains,
  TENANT_ID_TO_NAME,
} from '../utils/constants';
import { jsonParse } from '../utils/json';
import { win } from '../utils/win';
import { launchDarklyKeyMap } from './launchDarklyKeyMap';

/**
 * Core config types and hostname-derivation primitives.
 *
 * This module is a lower layer shared by the registry-driven config builder
 * (`./tenants/buildConfig`) and the connect-app config (`./connect`).
 */

export interface Config {
  alchemyDataEndpoint: string;
  apiEndpoint: string;
  minionApiRestEndpoint: string;
  dataDictionaryEndpoint: string;
  uploadsApiEndpoint: string;
  launchDarklyKey: string;
  keycloakUri: string;
  keycloakRealm: string;
  keycloakClientId: string;
  restApiEndpoint: string;
  subscriptionApiEndpoint: string;
  emailTenderingEndpoint: string;
  aiEndpoint: string;
  caenLockingApiEndpoint: string;
  regionsDataEndpoint: string;
  ltlPricingMatrix: string;
  flatFileLicenseKey: string;
  flatFileApiEndPoint: string;
  masterbidEndpoint: string;
  flatFilePlatformPublishableKey: string;
  flatFilePlatformEnvironmentId: string;
  /** ie chs9l-test, ml100-prod */
  envKey: string;
  documentApiEndpoint: string;
}

export const crossAppKeys = [
  'apiEndpoint',
  'keycloakUri',
  'keycloakRealm',
  'keycloakClientId',
  'launchDarklyKey',
  'envKey',
] as const;
export type CrossAppConfig = Pick<Config, (typeof crossAppKeys)[number]>;

export const configAliases: Partial<Record<KnownDomains, KnownDomains[]>> = {
  'ml100.mastermindtms.com': [
    'molo.mastermindtms.com',
    'mm.shipmolo.com',
    'arcbest.mastermindtms.com',
  ],
  'test.ml100.mastermindtms.com': [
    'sandbox.shipmolo.com',
    'upsidedown.shipmolo.com',
    'test.arcbest.mastermindtms.com',
  ],
  'dev.mm100.mastermindtms.com': [
    'mastery-frontend.dev.mm100.mastermindtms.com',
  ],
};

const getTenantCodeFromHostname = (hostname: string): string | undefined => {
  if (hostname.match(/connect-carrier/)) {
    return 'connect-carrier';
  }
  if (hostname.match(/connect-customer/)) {
    return 'connect-customer';
  }
  const found = Object.entries(TENANT_ID_TO_NAME).find(([, name]) =>
    new RegExp(name).test(hostname),
  )?.[0];
  if (found) {
    return found;
  }
  let base = typeof hostname === 'string' ? hostname : '';
  const foundFromResolutions = Object.entries(configAliases).find(
    ([, testArr]) => testArr.includes(base as KnownDomains),
  )?.[0];
  if (foundFromResolutions) {
    base = foundFromResolutions;
  }
  return base
    .replace('.mastermindtms.com', '')
    .replace('dev.', '')
    .replace('test.', '');
};

export const getEnvKeyFromHostname = (hostname: string): string => {
  if (hostname === 'localhost') {
    return hostname;
  }
  if (isPRPreview(hostname)) {
    return 'pull';
  }

  // Fix envKey for molo aliases on test.ml100.mastermindtms.com (was falling back on ml100-prod
  // for sandbox.shipmolo.com and upsidedown.shipmolo.com).
  let resolvedHostname = hostname;
  const foundFromResolutions = Object.entries(configAliases).find(
    ([, testArr]) => testArr.includes(resolvedHostname as KnownDomains),
  )?.[0];
  if (foundFromResolutions) {
    resolvedHostname = foundFromResolutions;
  }

  const tenantCode = getTenantCodeFromHostname(hostname);
  let res = '';
  if (resolvedHostname.match(/dev\./)) {
    res = `${tenantCode}-dev`;
  } else if (resolvedHostname.match(/test\./)) {
    res = `${tenantCode}-test`;
  } else {
    res = `${tenantCode}-prod`;
  }
  if (tenantCode === 'connect-carrier') {
    return res.replace('connect-carrier-', '');
  }
  if (tenantCode === 'connect-customer') {
    return res.replace('connect-customer-', '');
  }
  return res;
};

/**
 * Local-dev / cypress / PR-preview configuration primitives.
 *
 * These are shared by both the connect-app config and the registry-driven
 * `buildConfigFromRegistry`, so the localhost / cypress / PR-preview branches
 * stay byte-identical across both systems.
 */

const devPort = ENV_VARS.VITE_API_PORT || '5321';
const localHttps = IS_LOCAL_DEV && win.location.protocol === 'https:';
const localHttpProtocol = localHttps ? 'https' : 'http';
const localWebSocketProtocol = localHttps ? 'wss' : 'ws';

export const getPRDomainFromStorage = (): string | undefined => {
  if (isPRPreview(win.location.hostname)) {
    if (win.localStorage?.getItem(KEY_USER_PREFERENCES)) {
      const prefs = jsonParse<{ prDomain?: string }>(
        win.localStorage.getItem(KEY_USER_PREFERENCES) || '{}',
      );
      return prefs?.prDomain;
    }
  }
  return;
};

const {
  VITE_GRAPHQL_URL: apiEndpoint = '',
  VITE_DATA_DICTIONARY_URL:
    dataDictionaryEndpoint = `${localHttpProtocol}://localhost:${devPort}/minion`,
  VITE_MINION_API_URL:
    minionApiRestEndpoint = `${localHttpProtocol}://localhost:${devPort}/minion-api`,
  VITE_UPLOADS_URL: uploadsApiEndpoint = '',
  VITE_AUTH_KEYCLOAK_REALM: keycloakRealm = 'dev.mm100.mastermindtms.com',
  VITE_AUTH_KEYCLOAK_SERVER:
    keycloakUri = 'https://id.mm100nonprod.mastermindtms.com/auth',
  VITE_AUTH_KEYCLOAK_CLIENTID:
    keycloakClientId = 'https://dev.mm100.mastermindtms.com/',
  VITE_REST_URL: restApiEndpoint = '',
  VITE_SUBSCRIPTION_ENDPOINT:
    subscriptionApiEndpoint = `${localWebSocketProtocol}://localhost:${devPort}/graphql`,
  VITE_EMAIL_TENDERING_ENDPOINT:
    emailTenderingEndpoint = 'https://tendering-email-api.dev.mm100.mastermindtms.com',
  VITE_AI_ENDPOINT: aiEndpoint = 'https://ai.dev.mm100.mastermindtms.com',
  VITE_AlCHEMY_DATA_ENDPOINT:
    alchemyDataEndpoint = 'https://alchemy-data-api.dev.mm100.mastermindtms.com',
  VITE_CAEN_LOCKING_ENDPOINT:
    caenLockingApiEndpoint = 'https://caenlocking-api.dev.mm100.mastermindtms.com',
  VITE_REGIONS_DATA_ENDPOINT:
    regionsDataEndpoint = `https://regions-v2-api.dev.mm100.mastermindtms.com`,
  VITE_FLAT_FILE_LICENSE_KEY: flatFileLicenseKey = '',
  VITE_FLAT_FILE_API_END_POINT:
    flatFileApiEndPoint = `https://flatfile.dev.mm100.mastermindtms.com`,
  VITE_LTL_PRICING_MATRIX_ENDPOINT:
    ltlPricingMatrix = `https://ltl-pricing-matrix.dev.mm100.mastermindtms.com`,
  VITE_MASTERBID_ENDPOINT:
    masterbidEndpoint = `https://masterbid.dev.mm100.mastermindtms.com`,
  VITE_FLATFILE_PLATFORM_PUBLISHABLE_KEY: flatFilePlatformPublishableKey = '',
  VITE_FLATFILE_PLATFORM_ENVIRONMENT_ID: flatFilePlatformEnvironmentId = '',
  VITE_DOCUMENT_API_URL: documentApiEndpoint = '',
} = ENV_VARS;

let localConfigValue: Config = {
  apiEndpoint,
  minionApiRestEndpoint,
  dataDictionaryEndpoint,
  keycloakClientId,
  keycloakRealm,
  keycloakUri,
  launchDarklyKey: launchDarklyKeyMap.local,
  restApiEndpoint,
  subscriptionApiEndpoint,
  alchemyDataEndpoint,
  uploadsApiEndpoint,
  emailTenderingEndpoint,
  aiEndpoint,
  caenLockingApiEndpoint,
  regionsDataEndpoint,
  ltlPricingMatrix,
  flatFileLicenseKey,
  flatFileApiEndPoint,
  masterbidEndpoint,
  flatFilePlatformPublishableKey,
  flatFilePlatformEnvironmentId,
  documentApiEndpoint,
  envKey: 'local',
};

if (localHttps) {
  localConfigValue = mapValues(localConfigValue, (val) => {
    if (isString(val) && val.match('//')) {
      try {
        const url = new URL(val);
        if (['localhost', '127.0.0.1', '[::1]'].includes(url.hostname)) {
          url.protocol = url.protocol.startsWith('ws') ? 'wss:' : 'https:';
          return url.href.replace(/\/$/, val.endsWith('/') ? '/' : '');
        }
      } catch {
        //
      }
    }
    return val;
  });
}

// Always use local Node API when running tests in cypress
// There is no need to use internal dev or the value of VITE_EMULATE_DOMAIN for cy.
if (IS_LOCAL_CYPRESS) {
  localConfigValue = mapValues(localConfigValue, (val) => {
    if (isString(val) && val.match('//')) {
      try {
        const uu = new URL(val);
        uu.host = `localhost:${devPort}`;
        uu.protocol = uu.protocol.startsWith('ws')
          ? `${localWebSocketProtocol}:`
          : `${localHttpProtocol}:`;
        let str = uu.href;
        if (!val.match(/\/$/) && uu.pathname === '/') {
          str = str.replace(/\/$/, '');
        }
        return str;
      } catch {
        //
      }
    }
    return val;
  });
}

export const localConfig: Config = localConfigValue;

export const hostnameIsV2Tenant = (hostname: string): boolean =>
  Boolean(
    Object.entries(TENANT_ID_TO_NAME).find(([, name]) =>
      new RegExp(name).test(hostname),
    ),
  );
